import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { addSnackbar, dismissSnackbar } from '../../../store/settings/actions'

import MaynLayout from '../../Core/MainLayout'
import PageLayout from '../../Core/PageLayout'
import TitleBlock from '../../Core/TitleBlock'
import Dashboard from '../../Core/Dashboard'
import InvoiceIcon from '../../Icons/Invoice'
import Input from '../../Core/Input/Input'
import InputLabel from '../../Core/InputLabel'
import SimpleSelect from '../../Core/SimpleSelect'
import Switch from 'react-switch'

import { getCurrencies, getInvoiceSettings, updateInvoiceSettings } from '../../../api'
import styles from './styles.module.scss'

const languagesOptions = [{id: 'en', title: 'English'}, {id: 'lt', title: 'Lithuanian'}, {id: 'es', title: 'Spanish'}, {id: 'sv', title: 'Swedish'}]

const InvoicesSettings = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { id: companyId } = useParams()

  const [currencies, setCurrencies] = useState([])
  const [form, setForm] = useState({
    invoice_prefix: '',
    next_invoice_number: 1,
    default_currency: '',
    default_vat: 0,
    default_work_markup: 0,
    default_expense_markup: 0,
    payment_terms: 7,
    default_language: '',
    auto_assign_to_project: false,
    lock_entries_on_issue: false,
    default_customer_notes: '',
    bank_name: '',
    swift_bit: '',
    account_number: '',
    ocr: '',
  })

  const termsOptions = [{id: 7, title: t('{{count}} days', {count: 7})}, {id: 10, title: t('{{count}} days', {count: 10})}, {id: 14, title: t('{{count}} days', {count: 14})}, {id: 21, title: t('{{count}} days', {count: 21})}, {id: 30, title: t('{{count}} days', {count: 30})}]

  useEffect(() => {
    getCurrencies().then(res => {
      if (Array.isArray(res)) {
        setCurrencies(res.map(c => ({id: c.code, title: `${c.code} - ${c.name}`})))
      }
    })
  }, [])

  useEffect(() => {
    getInvoiceSettings(companyId).then(res => {
      if (res) {
        setForm(prev => ({
          ...prev,
          invoice_prefix: res.invoice_prefix || '',
          next_invoice_number: res.next_invoice_number,
          default_currency: res.default_currency,
          default_vat: res.default_vat,
          payment_terms: res.payment_terms || 7,
          default_language: res.default_language ? res.default_language.toLowerCase() : 'en',
          auto_assign_to_project: res.auto_assign_to_project || false,
          lock_entries_on_issue: res.lock_entries_on_issue || false,
          default_customer_notes: res.default_customer_notes || '',
          default_work_markup: res.default_work_markup || 0,
          default_expense_markup: res.default_expense_markup || 0,
          bank_name: res.bank_name || '',
          swift_bit: res.swift_bit || '',
          account_number: res.account_number || '',
          ocr: res.ocr || '',
        }))
      }
    })
  }, [companyId])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    const nextValue = type === 'checkbox' ? checked : value
    const nextForm = {
      ...form,
      [name]: nextValue,
    }

    setForm(nextForm)

    if (companyId) {
      updateInvoiceSettings(companyId, nextForm).catch(() => {
        // Keep the UI responsive even if the request fails.
      }).then(() => {
        dispatch(addSnackbar(t('Invoice settings updated')))
        setTimeout(() => {
          dispatch(dismissSnackbar())
        }, 3000)
      })
    }
  }

  return (
    <MaynLayout>
      <Dashboard>
        <TitleBlock
          title={t('Invoices settings')}
        >
          <InvoiceIcon width={44} height={44} fill='rgba(226,235,244,0.85)' />
        </TitleBlock>
        <PageLayout>
          <form className={styles.form} onSubmit={(e) => e.preventDefault()}>
            <div className={styles.section}>
              { t('Numbering') }
            </div>
            <div className={styles.field}>
              <InputLabel text={t('Invoice prefix')} htmlFor='invoice_prefix' />
              <Input
                id='invoice_prefix'
                name='invoice_prefix'
                value={form.invoice_prefix}
                onChange={handleInputChange}
                placeholder={t('Enter invoice prefix')}
                fullWidth
              />
            </div>
            <div className={styles.field}>
              <InputLabel text={t('Next invoice number')} htmlFor='next_invoice_number' />
              <Input
                id='next_invoice_number'
                name='next_invoice_number'
                type='number'
                min='1'
                value={form.next_invoice_number}
                onChange={handleInputChange}
                placeholder={t('Enter next invoice number')}
                fullWidth
              />
            </div>
            <div className={styles.section}>
              { t('Defaults') }
            </div>
            <div className={styles.field}>
              <InputLabel text={t('Default currency')} htmlFor='default_currency' />
              <SimpleSelect
                name='default_currency'
                value={form.default_currency}
                options={currencies}
                handleInputChange={handleInputChange}
                valueKey='id'
                labelKey='title'
                placeholder={t('Select currency')}
                withoutSearch
              />
            </div>
            <div className={styles.field}>
              <InputLabel text={`${t('Default VAT')} (%)`} htmlFor='default_vat' />
              <Input
                id='default_vat'
                name='default_vat'
                type='number'
                min='0'
                value={form.default_vat}
                onChange={handleInputChange}
                placeholder={t('Enter default VAT')}
                fullWidth
              />
            </div>
            <div className={styles.field}>
              <InputLabel text={`${t('Default work markup')} (%)`} htmlFor='default_work_markup' />
              <Input
                id='default_work_markup'
                name='default_work_markup'
                type='number'
                min='0'
                value={form.default_work_markup}
                onChange={handleInputChange}
                placeholder={t('Enter default work markup')}
                fullWidth
              />
            </div>
            <div className={styles.field}>
              <InputLabel text={`${t('Default expense markup')} (%)`} htmlFor='default_expense_markup' />
              <Input
                id='default_expense_markup'
                name='default_expense_markup'
                type='number'
                min='0'
                value={form.default_expense_markup}
                onChange={handleInputChange}
                placeholder={t('Default expense markup')}
                fullWidth
              />
            </div>
            <div className={styles.field}>
              <InputLabel text={t('Payment terms')} htmlFor='payment_terms' />
              <SimpleSelect
                name='payment_terms'
                value={form.payment_terms}
                options={termsOptions}
                handleInputChange={handleInputChange}
                valueKey='id'
                labelKey='title'
                placeholder={t('Select payment terms')}
                withoutSearch
              />
            </div>
            <div className={styles.field}>
              <InputLabel text={t('Default language')} htmlFor='default_language' />
              <SimpleSelect
                name='default_language'
                value={form.default_language}
                options={languagesOptions}
                handleInputChange={handleInputChange}
                valueKey='id'
                labelKey='title'
                placeholder={t('Select language')}
                withoutSearch
              />
            </div>
            <div className={styles.section}>
              { t('Behaviour') }
            </div>
            <div className={styles.switchGroup}>
              <div className={styles.switchRow}>
                <Switch
                  onChange={(checked) => handleInputChange({
                    target: {
                      name: 'auto_assign_to_project',
                      checked,
                      type: 'checkbox',
                    },
                  })}
                  offColor='#808F94'
                  onColor='#0085FF'
                  uncheckedIcon={false}
                  checkedIcon={false}
                  checked={!!form.auto_assign_to_project}
                  height={21}
                  width={40}
                />
                <InputLabel text={t('Auto assign invoice to project for income reporting')} />
              </div>
              <div className={styles.switchRow}>
                <Switch
                  onChange={(checked) => handleInputChange({
                    target: {
                      name: 'lock_entries_on_issue',
                      checked,
                      type: 'checkbox',
                    },
                  })}
                  offColor='#808F94'
                  onColor='#0085FF'
                  uncheckedIcon={false}
                  checkedIcon={false}
                  checked={!!form.lock_entries_on_issue}
                  height={21}
                  width={40}
                />
                <InputLabel text={t('Lock time entries after invoice issue')} />
              </div>
            </div>
            <div className={styles.section}>
              { t('Payment information') }
            </div>
            <div className={styles.field}>
              <InputLabel text={t('Bank name')} htmlFor='bank_name' />
              <Input
                id='bank_name'
                name='bank_name'
                value={form.bank_name}
                onChange={handleInputChange}
                placeholder={t('Enter bank name')}
                fullWidth
              />
            </div>
            <div className={styles.field}>
              <InputLabel text={t('SWIFT/BIC')} htmlFor='swift_bit' />
              <Input
                id='swift_bit'
                name='swift_bit'
                value={form.swift_bit}
                onChange={handleInputChange}
                placeholder={t('Enter SWIFT/BIC')}
                fullWidth
              />
            </div>
            <div className={styles.field}>
              <InputLabel text={t('Account number')} htmlFor='account_number' />
              <Input
                id='account_number'
                name='account_number'
                value={form.account_number}
                onChange={handleInputChange}
                placeholder={t('Enter account number')}
                fullWidth
              />
            </div>
            <div className={styles.field}>
              <InputLabel text={t('Enter OCR')} htmlFor='ocr' />
              <Input
                id='ocr'
                name='ocr'
                value={form.ocr}
                onChange={handleInputChange}
                placeholder={t('Enter OCR')}
                fullWidth
              />
            </div>
            <div className={styles.section}>
              { t('Default notes to customer') }
            </div>
            <div className={styles.field}>
              <Input
                id='default_customer_notes'
                name='default_customer_notes'
                value={form.default_customer_notes}
                onChange={handleInputChange}
                placeholder={t('Enter default notes visible on invoice')}
                fullWidth
              />
            </div>
          </form>
        </PageLayout>
      </Dashboard>
    </MaynLayout>
  )
}

export default InvoicesSettings
